/**
 * Typed fetch client for the control-plane API.
 *
 * - baseURL '/api' — the vite dev server proxies to :8000, nginx in prod.
 * - Access/refresh tokens held in memory and mirrored to localStorage.
 * - On a 401 the client refreshes once (single-flight across concurrent
 *   requests) and retries the original request; if the refresh fails the
 *   tokens are cleared and onUnauthorized listeners fire so the auth
 *   provider can route to /login.
 */

import type {
  Agent,
  AgentSummary,
  AgentWrite,
  Analytics,
  AuthResponse,
  AvailableNumber,
  CallDetail,
  CallListFilters,
  CallSummary,
  DocumentInfo,
  DocumentUploadResponse,
  Paginated,
  PhoneNumber,
  RefreshResponse,
  TestSession,
  User,
  VoiceEngine,
} from "./types";

const BASE_URL = "/api";
const ACCESS_KEY = "va.access_token";
const REFRESH_KEY = "va.refresh_token";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

// ---------------------------------------------------------------------------
// Token store — memory first, localStorage as the durable mirror
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let refreshToken: string | null = null;

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null; // private mode / storage disabled — memory-only session
  }
}

function loadTokens(): void {
  const store = safeStorage();
  if (!store) return;
  accessToken = store.getItem(ACCESS_KEY);
  refreshToken = store.getItem(REFRESH_KEY);
}
loadTokens();

export function setTokens(access: string | null, refresh: string | null): void {
  accessToken = access;
  refreshToken = refresh;
  const store = safeStorage();
  if (!store) return;
  if (access) store.setItem(ACCESS_KEY, access);
  else store.removeItem(ACCESS_KEY);
  if (refresh) store.setItem(REFRESH_KEY, refresh);
  else store.removeItem(REFRESH_KEY);
}

export function clearTokens(): void {
  setTokens(null, null);
}

export function hasTokens(): boolean {
  return accessToken !== null || refreshToken !== null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ---------------------------------------------------------------------------
// Unauthorized listeners — the auth provider subscribes to route to /login
// ---------------------------------------------------------------------------

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function emitUnauthorized(): void {
  for (const listener of unauthorizedListeners) listener();
}

// ---------------------------------------------------------------------------
// Core request machinery
// ---------------------------------------------------------------------------

export type Query = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown; // JSON-serialized unless FormData
  query?: Query;
  signal?: AbortSignal;
  /** Skip Authorization header + refresh logic (auth endpoints). */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: Query): string {
  const url = path.startsWith("/") ? `${BASE_URL}${path}` : `${BASE_URL}/${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function errorMessage(status: number, payload: unknown): string {
  if (payload && typeof payload === "object") {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      // FastAPI validation errors: [{loc, msg, type}, ...]
      const first = detail[0] as { msg?: string; loc?: unknown[] };
      if (first?.msg) {
        const field = Array.isArray(first.loc) ? first.loc.slice(1).join(".") : "";
        return field ? `${field}: ${first.msg}` : first.msg;
      }
    }
    const message = (payload as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  if (typeof payload === "string" && payload.length > 0 && payload.length < 300) return payload;
  return `Request failed (${status})`;
}

/** A "successful" auth call without tokens means something other than the
 * control plane answered (foreign server on :8000, proxy misroute). Failing
 * here keeps the app from proceeding as logged-in with undefined tokens —
 * which would otherwise surface pages later as confusing fetch crashes. */
function assertAuthResponse(res: unknown): asserts res is AuthResponse {
  const token = (res as { access_token?: unknown } | null)?.access_token;
  if (typeof token !== "string" || token.length === 0) {
    throw new ApiError(
      200,
      "Unexpected response from the auth endpoint — is the control plane running on port 8000?",
      res,
    );
  }
}

/** Single-flight refresh — concurrent 401s share one refresh round-trip. */
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) return false;
      const data = (await parseBody(response)) as RefreshResponse | null;
      if (!data?.access_token) return false;
      // Rotating refresh tokens: keep the new one when the server sends it.
      setTokens(data.access_token, data.refresh_token ?? refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Allow the next expiry to trigger a fresh refresh.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

async function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  const { method = "GET", body, query, signal, anonymous } = options;
  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;

  if (body instanceof FormData) {
    payload = body; // browser sets the multipart boundary
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  if (!anonymous && accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  return fetch(buildUrl(path, query), { method, headers, body: payload, signal });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await rawFetch(path, options);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await rawFetch(path, options);
    } else {
      clearTokens();
      emitUnauthorized();
    }
  }

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response.status, payload), payload);
  }
  // A 2xx that isn't JSON is never our API — it's the SPA fallback or some
  // other process answering on the control plane's port. Without this guard
  // the HTML lands in component state and crashes the first .filter/.map;
  // with it, the page's normal error path renders something actionable.
  const contentType = response.headers.get("content-type") ?? "";
  if (payload !== null && !contentType.includes("application/json")) {
    throw new ApiError(
      response.status,
      "The API returned non-JSON — is the control plane running on port 8000?",
      payload,
    );
  }
  return payload as T;
}

/** Fetch a non-JSON endpoint (file downloads) with the same auth + refresh
 * flow as request(). The JSON content-type guard deliberately does not apply. */
async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  let response = await rawFetch(path, options);
  if (response.status === 401 && !options.anonymous) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await rawFetch(path, options);
    } else {
      clearTokens();
      emitUnauthorized();
    }
  }
  if (!response.ok) {
    const payload = await parseBody(response);
    throw new ApiError(response.status, errorMessage(response.status, payload), payload);
  }
  return response.blob();
}

// ---------------------------------------------------------------------------
// Typed endpoint helpers — one per route in BUILD_PLAN §7
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    async register(input: { email: string; password: string; name: string }): Promise<AuthResponse> {
      const res = await request<AuthResponse>("/auth/register", {
        method: "POST",
        body: input,
        anonymous: true,
      });
      assertAuthResponse(res);
      setTokens(res.access_token, res.refresh_token);
      return res;
    },
    async login(input: { email: string; password: string }): Promise<AuthResponse> {
      const res = await request<AuthResponse>("/auth/login", {
        method: "POST",
        body: input,
        anonymous: true,
      });
      assertAuthResponse(res);
      setTokens(res.access_token, res.refresh_token);
      return res;
    },
    async logout(): Promise<void> {
      try {
        await request<void>("/auth/logout", { method: "POST" });
      } finally {
        clearTokens();
      }
    },
    me(signal?: AbortSignal): Promise<User> {
      return request<User>("/auth/me", { signal });
    },
  },

  agents: {
    /** Flat summaries — engine/number/7d counts pre-resolved; no config. */
    list(signal?: AbortSignal): Promise<AgentSummary[]> {
      return request<AgentSummary[]>("/agents", { signal });
    },
    get(id: string, signal?: AbortSignal): Promise<Agent> {
      return request<Agent>(`/agents/${id}`, { signal });
    },
    create(input: AgentWrite): Promise<Agent> {
      return request<Agent>("/agents", { method: "POST", body: input });
    },
    update(id: string, input: AgentWrite): Promise<Agent> {
      return request<Agent>(`/agents/${id}`, { method: "PATCH", body: input });
    },
    remove(id: string): Promise<void> {
      return request<void>(`/agents/${id}`, { method: "DELETE" });
    },
    duplicate(id: string): Promise<Agent> {
      return request<Agent>(`/agents/${id}/duplicate`, { method: "POST" });
    },
    analytics(id: string, range = "7d", signal?: AbortSignal): Promise<Analytics> {
      return request<Analytics>(`/agents/${id}/analytics`, { query: { range }, signal });
    },
    /** Spawns a runtime bot; returns SmallWebRTC signaling info (§12). */
    createTestSession(id: string, engineOverride?: VoiceEngine): Promise<TestSession> {
      return request<TestSession>(`/agents/${id}/test-session`, {
        method: "POST",
        body: engineOverride ? { engine_override: engineOverride } : {},
      });
    },
    /** Initiate an outbound call from this agent. */
    call(id: string, toNumber: string): Promise<CallSummary> {
      return request<CallSummary>(`/agents/${id}/calls`, {
        method: "POST",
        body: { to_number: toNumber },
      });
    },
  },

  documents: {
    list(agentId: string, signal?: AbortSignal): Promise<DocumentInfo[]> {
      return request<DocumentInfo[]>(`/agents/${agentId}/documents`, { signal });
    },
    upload(agentId: string, file: File): Promise<DocumentUploadResponse> {
      const form = new FormData();
      form.append("file", file, file.name);
      return request<DocumentUploadResponse>(`/agents/${agentId}/documents`, {
        method: "POST",
        body: form,
      });
    },
    remove(id: string): Promise<void> {
      return request<void>(`/documents/${id}`, { method: "DELETE" });
    },
    reprocess(id: string): Promise<DocumentInfo> {
      return request<DocumentInfo>(`/documents/${id}/reprocess`, { method: "POST" });
    },
  },

  numbers: {
    search(country = "US", areaCode?: string, signal?: AbortSignal): Promise<AvailableNumber[]> {
      return request<AvailableNumber[]>("/numbers/available", {
        query: { country, area_code: areaCode },
        signal,
      });
    },
    /** country = ISO-2 the number was searched in (backend requires it, §11). */
    buy(phoneNumber: string, country: string): Promise<PhoneNumber> {
      return request<PhoneNumber>("/numbers/buy", {
        method: "POST",
        body: { phone_number: phoneNumber, country },
      });
    },
    /**
     * Adopt a number the Twilio account already owns, repointing its webhooks
     * at this platform. Separate from buy() on purpose — one spends money.
     */
    import(phoneNumber: string, country?: string): Promise<PhoneNumber> {
      return request<PhoneNumber>("/numbers/import", {
        method: "POST",
        body: { phone_number: phoneNumber, country: country ?? null },
      });
    },
    list(signal?: AbortSignal): Promise<PhoneNumber[]> {
      return request<PhoneNumber[]>("/numbers", { signal });
    },
    /** Assign to an agent, or pass null to unassign. */
    assign(id: string, agentId: string | null): Promise<PhoneNumber> {
      return request<PhoneNumber>(`/numbers/${id}`, {
        method: "PATCH",
        body: { agent_id: agentId },
      });
    },
    release(id: string): Promise<void> {
      return request<void>(`/numbers/${id}`, { method: "DELETE" });
    },
  },

  calls: {
    /**
     * Start an outbound call from an agent to a phone number.
     *
     * Validated against the agent's SAVED config, not the editor draft:
     * outbound_enabled must be on and a number assigned, or this 400s.
     */
    create(agentId: string, toNumber: string): Promise<CallSummary> {
      return request<CallSummary>(`/agents/${agentId}/calls`, {
        method: "POST",
        body: { to_number: toNumber },
      });
    },
    list(filters: CallListFilters = {}, signal?: AbortSignal): Promise<Paginated<CallSummary>> {
      return request<Paginated<CallSummary>>("/calls", {
        query: filters as unknown as Query,
        signal,
      });
    },
    /** The session's captured runtime log timeline as a .log file blob. */
    downloadLogs(id: string): Promise<Blob> {
      return requestBlob(`/calls/${id}/logs/download`);
    },
    get(id: string, signal?: AbortSignal): Promise<CallDetail> {
      return request<CallDetail>(`/calls/${id}`, { signal });
    },
  },
};

export type Api = typeof api;
